import { useState, useEffect, useRef } from "react";
import * as THREE from "three";
import AnimateOnScroll from "./AnimateOnScroll";

type Message = { role: "user" | "ai"; text: string };

const chatMessages: Record<string, Message[]> = {
  Retirement: [
    { role: "user", text: "When will I be able to retire?" },
    {
      role: "ai",
      text: "You're looking to plan your retirement, that's a great step towards securing your future. To get started, I’ll step you through the data that I need from you to help me understand your timeline and provide more tailored guidance.",
    },
    {
      role: "ai",
      text: "I can see that you have £30,000 in your employer’s pension, do you have any other pensions? For example, from previous employers?",
    },
    {
      role: "user",
      text: "Yep, I’ve uploaded my last benefit statement here.",
    },
    {
      role: "ai",
      text: "Great, I've crunched the numbers for a first plan, see the forecast outcomes here.",
    },
  ],
  Investment: [
    {
      role: "user",
      text: "I've got some savings, but they are not earning much interest.  How can I make my money work harder for me?",
    },
    {
      role: "ai",
      text: "You're looking to make your savings work harder, which is a great goal. Before we dive into that, could you tell me a bit about yourself starting with how much you have in savings?",
    },
    { role: "user", text: " I've got about 14k in a savings account." },
    {
      role: "ai",
      text: "Great, do you want me to show you a comparison of what you could get if you invest rather than a savings account?",
    },
  ],
  Inheritance: [
    {
      role: "user",
      text: "I've given you all my retirement details, thanks for the forecast.  It’ts looking pretty good. How much might be left for my children when I die?",
    },
    {
      role: "ai",
      text: "Looking at the numbers, on average when both you and your partner have passed away, your estate is valued at £1.4m. This is likely to have inheritance tax payable. Would you like to look at your options around this?",
    },
    {
      role: "user",
      text: "Yes, please, give me some ideas for how I might be able to manage my tax bill.  I’d rather my family inherit the money I’ve saved up rather than it go to the gov’t.",
    },
    {
      role: "ai",
      text: "Understood, do you want me to show you a few options for legacy planning?",
    },
  ],
  Home: [
    { role: "user", text: "I want to know when I can buy my first home." },
    {
      role: "ai",
      text: "Ok, first off, how much do you have saved for a deposit?",
    },
    { role: "user", text: "I've got about 10,000 in a savings account." },
    {
      role: "ai",
      text: "Great, that's a good start. Do you know how much you think you will spend on your first home?",
    },
    { role: "user", text: " I don't know, maybe about 300k." },
    {
      role: "ai",
      text: "Ok, based on what you have told me, it will take about another 5 years to save up for the deposit.  Would you like to explore options you might have to achieve this sooner?",
    },
  ],
};

const DELAY_MS = 2000;

export default function HeroSection() {
  const [activeTopic, setActiveTopic] = useState("Retirement");
  const [visibleCount, setVisibleCount] = useState(0);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const webglRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = webglRef.current;
    if (!container) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xefebe5);

    const camera = new THREE.PerspectiveCamera(
      45,
      window.innerWidth / window.innerHeight,
      1,
      1000,
    );
    camera.position.set(0, 0, 60);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(renderer.domElement);

    const numLines = 3000;
    const segmentsPerLine = 80;
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(numLines * segmentsPerLine * 3);
    let pIndex = 0;
    const height = 50;
    const radiusBase = 18;

    for (let i = 0; i < numLines; i++) {
      const angleOffset = Math.random() * Math.PI * 2;
      const radiusOffset = Math.random();
      const curveFactor = Math.random() * 0.5 + 0.5;
      for (let j = 0; j < segmentsPerLine; j++) {
        const t = j / (segmentsPerLine - 1);
        const y = (t - 0.5) * height;
        let normalizedY = y / (height / 2);
        let pinch = Math.pow(Math.abs(normalizedY), 1.8);
        pinch = pinch * 0.95 + 0.05;
        const currentRadius = radiusBase * pinch * (radiusOffset * 0.5 + 0.5);
        const twist = normalizedY * 1.5 * curveFactor;
        const currentAngle = angleOffset + twist;
        let x = Math.cos(currentAngle) * currentRadius;
        let z = Math.sin(currentAngle) * currentRadius;
        const noiseScale = 0.5 * (1 - pinch);
        x += Math.sin(y * 0.5 + i * 0.1) * noiseScale;
        z += Math.cos(y * 0.3 + i * 0.1) * noiseScale;
        x += normalizedY * 4.0;
        positions[pIndex++] = x;
        positions[pIndex++] = y;
        positions[pIndex++] = z;
      }
    }

    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));

    const material = new THREE.LineBasicMaterial({
      color: 0x462f0c,
      transparent: true,
      opacity: 2,
      blending: THREE.NormalBlending,
      depthWrite: false,
    });

    const indices: number[] = [];
    for (let i = 0; i < numLines; i++) {
      for (let j = 0; j < segmentsPerLine - 1; j++) {
        const a = i * segmentsPerLine + j;
        const b = i * segmentsPerLine + j + 1;
        indices.push(a, b);
      }
    }
    geometry.setIndex(indices);

    const linesMesh = new THREE.LineSegments(geometry, material);
    scene.add(linesMesh);
    linesMesh.rotation.z = -0.1;
    linesMesh.position.x = 3;

    const updateMeshPosition = () => {
      const isMobile = window.innerWidth < 768;
      linesMesh.position.y = isMobile ? -10 : 0;
    };
    updateMeshPosition();

    const startTime = performance.now();
    let animId: number;

    const animate = () => {
      animId = requestAnimationFrame(animate);
      const elapsedTime = (performance.now() - startTime) / 1000;
      linesMesh.rotation.y = elapsedTime * 0.05;
      material.opacity = 0.012 + Math.sin(elapsedTime * 0.5) * 0.003;
      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
      updateMeshPosition();
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", onResize);
      renderer.dispose();
      geometry.dispose();
      material.dispose();
      container.removeChild(renderer.domElement);
    };
  }, []);

  const startAnimation = (topic: string) => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    setVisibleCount(0);
    if (containerRef.current) containerRef.current.scrollTop = 0;

    chatMessages[topic].forEach((_, i) => {
      const t = setTimeout(() => setVisibleCount(i + 1), i * DELAY_MS);
      timersRef.current.push(t);
    });
  };

  useEffect(() => {
    startAnimation(activeTopic);
    return () => timersRef.current.forEach(clearTimeout);
  }, [activeTopic]);

  useEffect(() => {
    if (visibleCount > 0 && containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [visibleCount]);

  const scrollToRegister = () => {
    const el = document.querySelector("#register");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToCoCreator = () => {
    const el = document.querySelector("#co-creator");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const messages = chatMessages[activeTopic];

  return (
    <section
      id="hero-section"
      className="relative min-h-screen bg-[#EFEBE5] flex items-center pt-16 overflow-hidden"
    >
      {/* webgl lines animation  */}
      {/* <div ref={webglRef} className="absolute inset-0 pointer-events-none" /> */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 py-12 sm:py-20 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center w-full">
        {/* Left */}
        <AnimateOnScroll variant="fade-left" delay={0}>
          {/* Announcement banner */}
          {/* <button
            onClick={scrollToCoCreator}
            className="inline-flex items-center gap-2 mb-5 bg-white border border-[#EDE8DF] rounded-full px-1.5 py-1.5 hover:border-[#C5AB85] transition-colors cursor-pointer group max-w-full overflow-hidden"
          >
            <span className="flex items-center gap-1.5 bg-brown-100 rounded-full px-2 py-0.5 flex-shrink-0">
              <i className="ri-vip-crown-line text-[10px] text-amber-400"></i>
              <span className="text-[10px] font-bold text-white tracking-wide uppercase whitespace-nowrap">
                Founding Offer
              </span>
            </span>
            <span className="text-xs text-[#4a4a4a] font-medium truncate">
              75% off through 2026 as a Co-Creator
            </span>
            <i className="ri-arrow-right-line text-xs text-[#9a9aaa] group-hover:translate-x-0.5 transition-transform flex-shrink-0"></i>
          </button> */}

          <p className="text-xs font-bold tracking-widest text-[#c4875a] uppercase mb-3">
            Conversational AI Built for UK Financial Planners
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a1a2e] leading-tight mb-6">
            The Future of Financial Planning is Conversational
            <span className="block text-lg sm:text-xl lg:text-2xl text-[#8a8a9a] mt-3 leading-snug">
              Intuitive enough that anyone on your team gets it from day one.
            </span>
          </h1>
          <p className="text-base text-[#5a5a6a] mb-8 leading-relaxed max-w-lg"></p>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <button
              onClick={scrollToRegister}
              className="shadow-sm inline-flex items-center justify-center gap-2 bg-[#222222] text-white text-md font-semibold px-6 py-3 rounded-full hover:bg-[#000000] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-add-line"></i>Get Started
            </button>
          </div>
        </AnimateOnScroll>

        {/* Right — Chat Widget */}
        <AnimateOnScroll variant="fade-right" delay={150}>
          <div className="flex justify-center lg:justify-end">
            <div className="bg-white shadow-lg rounded-2xl w-full max-w-md p-4 sm:p-5 border border-oat-2 overflow-hidden">
              {/* Topic pills */}
              <div className="flex gap-2 flex-wrap mb-4 sm:mb-5">
                {Object.keys(chatMessages).map((t) => (
                  <button
                    key={t}
                    onClick={() => setActiveTopic(t)}
                    className={`text-xs font-medium px-3 py-1 rounded border-0 transition-all cursor-pointer whitespace-nowrap ${
                      activeTopic === t
                        ? "bg-brown-500 text-white"
                        : "bg-brown-100 text-black"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>

              {/* Messages */}
              <div
                ref={containerRef}
                className="flex flex-col h-[200px] sm:h-[220px] mb-4"
                style={
                  {
                    overflowY: "scroll",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                  } as React.CSSProperties
                }
              >
                <div className="flex-1" />
                <div className="flex flex-col gap-3">
                  {messages.slice(0, visibleCount).map((msg, i) => (
                    <div
                      key={`${activeTopic}-${i}`}
                      className={`flex flex-shrink-0 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                      style={{ animation: "chat-in 0.4s ease-out both" }}
                    >
                      <div
                        className={`text-md px-3 sm:px-4 py-3 leading-relaxed ${
                          msg.role === "user"
                            ? "bg-[#F0EBE0] text-[#1a1a2e] rounded rounded-tr"
                            : "bg-[#FFFFFF] text-[#333333] rounded rounded-tl"
                        }`}
                      >
                        {msg.text}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Input bar */}
              <div className="flex items-center gap-2 input-bar px-0 py-0">
                <input
                  placeholder="Ask anything about your financial plan..."
                  className="flex-1 text-xs sm:text-md bg-transparent text-[#1a1a2e] placeholder-[#9a9aaa] outline-none min-w-0"
                  readOnly
                />
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-attachment-2 text-[#9a9aaa] text-sm"></i>
                  </div> */}
                  <button className="flex items-center shadow-sm gap-1.5 bg-[#222222] text-white text-xs font-semibold px-6 py-3 rounded-full cursor-pointer whitespace-nowrap">
                    <i className="ri-mic-line text-xs"></i>Speak
                  </button>
                </div>
              </div>
            </div>
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
}
